import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    this.addEventListener('click', this.#keepStripClicks);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.removeEventListener('click', this.#keepStripClicks);
    clearTimeout(this.#stripTimeout);
  }

  /**
   * Adds one variant to the cart straight from the card: a size in the strip, "Add to cart", or the
   * touch plus on a one-variant product. It posts the card's own product form, so everything that
   * follows an add from the product page runs here too (fly-to-cart, the cart count, the drawer).
   * The strip shows the result: a busy size while the request runs, then "Added" or the error, which
   * the form's live region also announces.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event, targeted at the control.
   */
  addVariant = async (event) => {
    event.preventDefault();
    const control = event.target;
    if (!(control instanceof HTMLElement)) return;
    if (control.getAttribute('aria-disabled') === 'true' || control.getAttribute('aria-busy') === 'true') return;

    const variantId = control.dataset.variantId;
    const productForm = /** @type {HTMLElement & {refs?: {liveRegion?: HTMLElement}} | null} */ (
      this.querySelector('product-form-component')
    );
    const form = productForm?.querySelector('form');
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    if (!variantId || !productForm || !form || !input) return;

    input.value = variantId;
    input.disabled = false;

    /** @type {string | null} */
    let errorMessage = null;
    /** @type {CartLinesUpdateEvent | null} */
    let linesEvent = null;
    // Shopify's own message is a bare "Cart Error"; the description says what went wrong ("All 1
    // Silk Scarf Geko are in your cart."). Without one, the strip's own translated error is shown.
    const onError = (/** @type {Event & {detail?: {description?: string}}} */ errorEvent) => {
      errorMessage = errorEvent.detail?.description || null;
    };
    const onLines = (/** @type {Event} */ update) => {
      linesEvent = /** @type {CartLinesUpdateEvent} */ (update);
    };
    productForm.addEventListener(StandardEvents.cartError, onError);
    productForm.addEventListener(StandardEvents.cartLinesUpdate, onLines, { once: true });

    this.#setStripState(null);
    // aria-busy tells assistive tech; data-busy is the styling hook, so the CSS keys off an
    // attribute the static aria-styling-hooks gate can see being emitted.
    control.setAttribute('aria-busy', 'true');
    control.dataset.busy = '';
    this.#flyToCart(control);
    form.requestSubmit();

    let didError = false;
    try {
      /** @type {{detail?: {didError?: boolean}} | undefined} */
      const result = linesEvent ? await /** @type {CartLinesUpdateEvent} */ (linesEvent).promise : undefined;
      didError = !linesEvent || Boolean(result?.detail?.didError);
    } catch (error) {
      didError = true;
    } finally {
      productForm.removeEventListener(StandardEvents.cartError, onError);
      productForm.removeEventListener(StandardEvents.cartLinesUpdate, onLines);
      control.removeAttribute('aria-busy');
      delete control.dataset.busy;
    }

    const message = didError ? errorMessage || this.dataset.errorText || '' : this.dataset.addedText || '';
    this.#setStripState(didError ? 'error' : 'added', message);
    const liveRegion = productForm.refs?.liveRegion ?? productForm.querySelector('[role="status"]');
    if (liveRegion) liveRegion.textContent = message;

    clearTimeout(this.#stripTimeout);
    this.#stripTimeout = setTimeout(
      () => {
        this.#setStripState(null);
        if (liveRegion) liveRegion.textContent = '';
      },
      didError ? 6000 : 1800
    );
  };

  /** @type {number | undefined} */
  #stripTimeout;

  /**
   * Shows "Added" or an error across the strip, or clears it.
   * @param {'added' | 'error' | null} state
   * @param {string} [message]
   */
  #setStripState(state, message = '') {
    const strip = this.querySelector('.quick-add__strip');
    const status = strip?.querySelector('.quick-add__strip-status');
    if (!strip || !status) return;
    if (state) {
      strip.setAttribute('data-state', state);
      status.textContent = message;
    } else {
      strip.removeAttribute('data-state');
      status.textContent = '';
    }
  }

  /**
   * The theme's fly-to-cart picture, from the clicked control to the cart in the header, as the
   * product form's own Add button draws it (assets/product-form.js).
   * @param {HTMLElement} source
   */
  #flyToCart(source) {
    if (this.dataset.addToCartAnimation !== 'true') return;
    const image = this.dataset.productVariantMedia;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!image || !cartIcon) return;

    const flyToCart = /** @type {HTMLElement & {source: Element, destination: Element}} */ (
      document.createElement('fly-to-cart')
    );
    flyToCart.classList.add('fly-to-cart--quick');
    flyToCart.style.setProperty('background-image', `url(${image})`);
    flyToCart.style.setProperty('--start-opacity', '0');
    flyToCart.source = source;
    flyToCart.destination = cartIcon;
    document.body.appendChild(flyToCart);
  }

  /**
   * A click on the strip itself, between two sizes, must not fall through to the card and open
   * the product page: the strip is a control surface, not part of the picture.
   * @param {MouseEvent} event
   */
  #keepStripClicks = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.quick-add__strip') && !event.target.closest('a[href], button')) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
